<script setup lang="ts">
/**
 * 兌換碼 — 活動詳情（批104）。三個頁籤：**設定／代碼／兌換紀錄**。
 *
 * 與 `RedeemCampaignsView` 一樣是**兩個 realm 共用**（見 `lib/redeemApi.ts`）：
 *   - 我方：`/redeem-campaigns/:id`，superadmin 才改得動設定（後端 `require_superadmin`）。
 *   - 代理商：`/reseller/redeem-campaigns/:id`，**唯讀＋發放／作廢**（後端沒有修改端點），
 *     兌換紀錄不含 IP、活動不含內部備註。
 *
 * ★ CSV 匯出**由前端產生**：那是「把剛發的一批碼交給業務」的動作，資料已經在畫面上，
 *   為它加一支後端端點等於多一條會吐出憑據的路徑。
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { RedeemCampaign, RedeemCode, Redemption } from '@/api/ls'
import { buildCsv, downloadCsv } from '@/lib/csvSafe'
import { redeemKindLabel } from '@/lib/licenseLabels'
import { fmtModulesZh } from '@/lib/modules'
import { quotaSummaryText } from '@/lib/quotas'
import { useRedeem } from '@/lib/redeemApi'
import { safeHttpUrl } from '@/lib/safeUrl'
import { seedQuotas } from '@/lib/seedQuotas'
import { customerOptionLabel } from '@/lib/customerLabel'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const store = useDataStore()
const dialogs = useDialogsStore()
const { mode, api, pr } = useRedeem()

const campaignId = computed(() => String(route.params.id ?? ''))
const isReseller = computed(() => mode() === 'reseller')
/** 修改設定＝我方 superadmin（代理商 realm 連端點都沒有）。 */
const canEdit = computed(() => mode() === 'admin' && auth.isManagerOrAbove)

const campaign = ref<RedeemCampaign | null>(null)
const loading = ref(true)
const loadError = ref('')
const tab = ref<'settings' | 'codes' | 'redemptions'>('settings')

async function loadCampaign() {
  campaign.value = await api().get(campaignId.value)
  syncEdit()
}

onMounted(async () => {
  store.ensureProducts().catch(() => {})
  store.ensureCustomers().catch(() => {})
  if (!isReseller.value) store.ensureResellers().catch(() => {})
  try {
    await loadCampaign()
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取活動失敗')
  } finally {
    loading.value = false
  }
})

function errText(e: unknown, fallback: string): string {
  const d =
    e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return d
  const obj = d as { message?: string; code?: string }
  return obj.message ?? obj.code ?? JSON.stringify(d)
}
async function fail(e: unknown, fallback: string) {
  await dialogs.notify({ title: '操作失敗', message: errText(e, fallback) })
}

function fmtDate(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '—'
}
function fmtDateTime(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 16).replace('T', ' ') : '—'
}
function statusLabel(s: string): string {
  return { active: '進行中', paused: '已暫停' }[s] ?? s
}
function codeStatusLabel(s: string): string {
  return { issued: '未兌換', redeemed: '已兌換', void: '已作廢' }[s] ?? s
}
function codeStatusClass(s: string): string {
  return { issued: 'pending', redeemed: 'active', void: 'revoked' }[s] ?? ''
}
function channelLabel(c: string): string {
  return { product: '產品端', portal: '客戶 portal', link: '連結領取' }[c] ?? c
}
/** 發放來源：代理商清單上要看得出哪些是我方代發的（裁示 28）。 */
function realmLabel(r: string | null): string {
  if (r === 'staff') return '我方代發'
  if (r === 'reseller') return '代理商'
  return '—'
}
/**
 * 模組鍵 → 中文名。`effect_summary.modules` 是**鍵陣列**（後端排序過），這裡補成
 * `{key: '*'}` 再交給既有的 `fmtModulesZh`——憑證設計還沒載回來時它會退回原始英文鍵，
 * 不會壞（同 `LicensesView`）。
 */
function moduleNames(keys: string[] | undefined): string {
  const spec = store.productByKey(campaign.value?.product)
  return fmtModulesZh(spec, Object.fromEntries((keys ?? []).map((k) => [k, '*'])))
    .map((m) => m.text)
    .join('、')
}
/** 「歸屬」：null＝直營；admin 讀不到代理商清單時只說「代理商專屬」（不印 uuid 前綴）。 */
function ownerText(c: RedeemCampaign): string {
  if (!c.reseller_id) return '直營'
  return store.resellersDenied ? '代理商專屬' : store.resellerName(c.reseller_id)
}
/**
 * 活動效果裡的配額摘要（批114）。
 *
 * - 鍵名／中文名／要顯示哪幾個，全部由該產品的憑證設計驅動（`quotaSummaryText`）。
 * - 批114 之前建的活動，效果的數字是**一級欄位**（`effect_summary` 的頂層鍵），
 *   所以先用 `seedQuotas` 併進 `quotas` 再組字——否則舊活動的數字會憑空消失。
 * - 沒有任何宣告配額的產品回空字串，呼叫端整段省略。
 */
function effectQuotaText(c: RedeemCampaign): string {
  const spec = store.productByKey(c.product) ?? null
  return quotaSummaryText(spec, seedQuotas(spec, c.effect_summary))
}
function effectText(c: RedeemCampaign): string {
  const s = c.effect_summary
  const days = s.days ? `${s.days} 天` : '—'
  if (c.kind === 'extend') return `延長 ${days}`
  const mods = moduleNames(s.modules) || '不含模組'
  if (c.kind === 'trial') return `${s.label || '試用'}（${mods}）・${days}`
  const quotas = effectQuotaText(c)
  return `${s.plan_label || '試用授權'}（${mods}）${quotas ? `・${quotas}` : ''}・${days}`
}
/** 兌換紀錄那一列的效果：以**實際**前後到期日為準（不是活動的宣稱值）。 */
function redemptionEffect(r: Redemption): string {
  const s = r.effect_summary
  if (r.kind === 'extend') return `延長 ${s.days ?? '—'} 天`
  const mods = moduleNames(s.modules)
  return `${redeemKindLabel(r.kind)}${mods ? `・${mods}` : ''}・${s.days ?? '—'} 天`
}

// --- 設定頁籤 ---------------------------------------------------------------

const edit = ref({
  name: '',
  notes: '',
  quantity: '',
  unlimited: false,
  startsAt: '',
  endsAt: '',
  clearWindow: false,
  stackable: false,
  oncePer: 'deployment',
  targetLicense: 'any',
})
const saving = ref(false)

function syncEdit() {
  const c = campaign.value
  if (!c) return
  edit.value = {
    name: c.name,
    notes: c.notes ?? '',
    quantity: c.quantity == null ? '' : String(c.quantity),
    unlimited: c.quantity == null,
    startsAt: c.starts_at ? c.starts_at.slice(0, 10) : '',
    endsAt: c.ends_at ? c.ends_at.slice(0, 10) : '',
    clearWindow: false,
    stackable: c.stackable,
    oncePer: c.once_per ?? 'deployment',
    targetLicense: c.target_license,
  }
}

function toIso(day: string, end = false): string | null {
  if (!day) return null
  const d = new Date(`${day}T${end ? '23:59:59' : '00:00:00'}`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

async function saveSettings() {
  if (!campaign.value || saving.value) return
  const e = edit.value
  saving.value = true
  try {
    campaign.value = await ls.updateRedeemCampaign(campaign.value.id, {
      name: e.name.trim(),
      notes: e.notes.trim() || null,
      // 顯式清限量走 `unlimited`（`quantity: null` 在 JSON 裡與「沒送」分不出來）。
      unlimited: e.unlimited,
      quantity: e.unlimited ? undefined : Number(e.quantity) > 0 ? Number(e.quantity) : undefined,
      clear_window: !e.startsAt && !e.endsAt,
      starts_at: toIso(e.startsAt),
      ends_at: toIso(e.endsAt, true),
      stackable: e.stackable,
      once_per: e.oncePer,
      target_license: e.targetLicense,
    })
    syncEdit()
    await dialogs.notify({ title: '已儲存', message: '活動設定已更新。' })
  } catch (err) {
    await fail(err, '儲存失敗')
  } finally {
    saving.value = false
  }
}

async function toggleStatus() {
  const c = campaign.value
  if (!c) return
  const next = c.status === 'active' ? 'paused' : 'active'
  const ok = await dialogs.confirm({
    title: next === 'paused' ? '暫停活動' : '恢復活動',
    message:
      next === 'paused'
        ? `暫停「${c.name}」？暫停期間客戶兌換會收到「這檔兌換活動目前暫停中」，已發出的代碼不受影響。`
        : `恢復「${c.name}」？客戶又可以開始兌換了。`,
    confirmText: next === 'paused' ? '暫停' : '恢復',
    danger: next === 'paused',
  })
  if (!ok) return
  try {
    campaign.value = await ls.updateRedeemCampaign(c.id, { status: next })
    syncEdit()
  } catch (e) {
    await fail(e, '更新失敗')
  }
}

// --- 代碼頁籤 ---------------------------------------------------------------

const codes = ref<RedeemCode[]>([])
const codeCounts = ref<Record<string, number>>({})
const codesCursor = ref<string | null>(null)
const codesLoading = ref(false)
const codesLoaded = ref(false)
const codeStatusFilter = ref('')

async function loadCodes(cursor: string | null) {
  const page = await api().listCodes(campaignId.value, {
    cursor,
    status: codeStatusFilter.value || undefined,
  })
  codes.value = cursor ? [...codes.value, ...page.items] : page.items
  codeCounts.value = page.counts
  codesCursor.value = page.next_cursor
}

async function reloadCodes() {
  codesLoading.value = true
  try {
    await loadCodes(null)
    codesLoaded.value = true
  } catch (e) {
    await fail(e, '讀取代碼失敗')
  } finally {
    codesLoading.value = false
  }
}

async function moreCodes() {
  if (!codesCursor.value || codesLoading.value) return
  codesLoading.value = true
  try {
    await loadCodes(codesCursor.value)
  } catch (e) {
    await fail(e, '讀取下一頁失敗')
  } finally {
    codesLoading.value = false
  }
}

// --- 發放 ---
const issueOpen = ref(false)
const issuing = ref(false)
const issueDraft = ref({ count: 10, customerId: '', note: '' })
/** 剛發出的那一批——發完直接顯示在 modal 裡，方便整批複製給業務。 */
const justIssued = ref<RedeemCode[]>([])

function openIssue() {
  issueDraft.value = { count: 10, customerId: '', note: '' }
  justIssued.value = []
  issueOpen.value = true
}

const issueRemaining = computed(() => {
  const c = campaign.value
  if (!c || c.quantity == null) return null
  return Math.max(0, c.quantity - c.issued_count)
})

async function doIssue() {
  const c = campaign.value
  if (!c || issuing.value) return
  const n = Math.floor(Number(issueDraft.value.count))
  if (!(n >= 1 && n <= 500)) return
  issuing.value = true
  try {
    const res = await api().issueCodes(c.id, {
      count: n,
      to_customer_id: issueDraft.value.customerId || null,
      note: issueDraft.value.note.trim() || null,
    })
    justIssued.value = res.items
    campaign.value = res.campaign
    syncEdit()
    await reloadCodes()
  } catch (e) {
    await fail(e, '發放失敗')
  } finally {
    issuing.value = false
  }
}

async function voidCode(code: RedeemCode) {
  const c = campaign.value
  if (!c) return
  const ok = await dialogs.confirm({
    title: '作廢代碼',
    message: `作廢 ${code.display_code}？作廢後這組碼永遠不能兌換，名額會釋回一格可以重發。`,
    confirmText: '作廢',
    danger: true,
  })
  if (!ok) return
  try {
    const res = await api().voidCode(c.id, code.id)
    campaign.value = res.campaign
    syncEdit()
    await reloadCodes()
  } catch (e) {
    await fail(e, '作廢失敗')
  }
}

async function copyCode(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    await dialogs.notify({ title: '已複製', message: text })
  } catch {
    await dialogs.notify({ title: '複製失敗', message: `請手動複製：${text}` })
  }
}

/**
 * CSV 匯出（前端產生）：匯出**目前已載入**的那些代碼。
 * 經 `lib/csvSafe`：BOM（Excel 開啟時不把中文欄位解成亂碼）＋公式注入跳脫（發放備註、客戶名稱
 * 是代理商窗口寫得到的欄位，以 `= + - @` 開頭會被試算表當公式執行；批135-Q3）。
 */
function exportCsv() {
  const c = campaign.value
  if (!c) return
  const head = ['代碼', '狀態', '發給客戶', '發放備註', '發放人', '發放來源', '發放時間', '兌換時間']
  const rows = codes.value.map((row) => [
    row.display_code,
    codeStatusLabel(row.status),
    row.distributed_to_customer_id ? store.customerName(row.distributed_to_customer_id) : '',
    row.distributed_note ?? '',
    row.issued_by ?? '',
    realmLabel(row.issued_realm),
    fmtDateTime(row.issued_at ?? row.created_at),
    fmtDateTime(row.redeemed_at),
  ])
  downloadCsv(`redeem-${c.name}-codes.csv`, buildCsv(head, rows))
}

// --- 兌換紀錄頁籤 -----------------------------------------------------------

const redemptions = ref<Redemption[]>([])
const redCursor = ref<string | null>(null)
const redLoading = ref(false)
const redLoaded = ref(false)

async function loadRedemptions(cursor: string | null) {
  const page = await api().listRedemptions(campaignId.value, { cursor })
  redemptions.value = cursor ? [...redemptions.value, ...page.items] : page.items
  redCursor.value = page.next_cursor
}

async function reloadRedemptions() {
  redLoading.value = true
  try {
    await loadRedemptions(null)
    redLoaded.value = true
  } catch (e) {
    await fail(e, '讀取兌換紀錄失敗')
  } finally {
    redLoading.value = false
  }
}

async function moreRedemptions() {
  if (!redCursor.value || redLoading.value) return
  redLoading.value = true
  try {
    await loadRedemptions(redCursor.value)
  } catch (e) {
    await fail(e, '讀取下一頁失敗')
  } finally {
    redLoading.value = false
  }
}

/** 頁籤懶載：切過去才打那一支（活動詳情頁本來就三份獨立資料）。 */
watch(tab, async (t) => {
  if (t === 'codes' && !codesLoaded.value) await reloadCodes()
  if (t === 'redemptions' && !redLoaded.value) await reloadRedemptions()
})

function back() {
  router.push({ name: pr('redeem-campaigns') })
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <div class="title">
        <button class="back" type="button" @click="back()">← 兌換碼</button>
        <h1>{{ campaign?.name ?? '兌換活動' }}</h1>
        <span
          v-if="campaign"
          class="status-pill"
          :class="campaign.status === 'active' ? 'active' : 'revoked'"
        >
          {{ statusLabel(campaign.status) }}
        </span>
      </div>
      <div v-if="campaign && canEdit" class="head-acts">
        <UiButton :variant="campaign.status === 'active' ? 'warn' : 'secondary'" @click="toggleStatus()">
          {{ campaign.status === 'active' ? '暫停活動' : '恢復活動' }}
        </UiButton>
      </div>
    </div>

    <p v-if="loadError" class="err-line">{{ loadError }}</p>
    <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>

    <template v-else-if="campaign">
      <div class="tabs" data-hscroll>
        <button class="tab" :class="{ active: tab === 'settings' }" @click="tab = 'settings'">設定</button>
        <button class="tab" :class="{ active: tab === 'codes' }" @click="tab = 'codes'">代碼</button>
        <button class="tab" :class="{ active: tab === 'redemptions' }" @click="tab = 'redemptions'">
          兌換紀錄
        </button>
      </div>

      <!-- ===== 設定 ===== -->
      <div v-show="tab === 'settings'" class="card">
        <div class="card-head"><h3 class="section-title">活動內容</h3></div>
        <dl class="info">
          <div><dt>類型</dt><dd>{{ redeemKindLabel(campaign.kind) }}</dd></div>
          <div><dt>效果</dt><dd>{{ effectText(campaign) }}</dd></div>
          <div><dt>產品</dt><dd>{{ store.productName(campaign.product) }}</dd></div>
          <div v-if="!isReseller">
            <dt>歸屬</dt><dd>{{ ownerText(campaign) }}</dd>
          </div>
          <div>
            <dt>代碼形態</dt>
            <dd>
              {{ campaign.code_mode === 'shared' ? '共用碼' : '不重複子碼' }}
              <template v-if="campaign.code_prefix">
                （前綴 <code class="mono">{{ campaign.code_prefix }}</code>）
              </template>
            </dd>
          </div>
          <div v-if="campaign.shared_code">
            <dt>共用代碼</dt>
            <dd>
              <code class="mono big">{{ campaign.shared_code.display_code }}</code>
              <UiButton size="sm" @click="copyCode(campaign.shared_code.display_code)">複製</UiButton>
            </dd>
          </div>
          <!-- 批107：代理商看得到條件（他要據此跟客戶解釋），但只有我方改得動。 -->
          <div>
            <dt>使用條件</dt>
            <dd>{{ campaign.once_per === 'customer' ? '單一客戶一次' : '單一部署一次' }}</dd>
          </div>
          <div><dt>已兌換</dt><dd>{{ campaign.redeemed_count }} 次</dd></div>
          <div v-if="campaign.code_mode === 'unique'">
            <dt>已發放</dt><dd>{{ campaign.issued_count }} 組</dd>
          </div>
          <div><dt>建立者</dt><dd>{{ campaign.created_by ?? '—' }}（{{ fmtDate(campaign.created_at) }}）</dd></div>
        </dl>
        <p class="locked-note">
          產品、類型、代碼形態、代理商歸屬、子碼前綴<strong>建立後鎖定</strong>；
          <template v-if="campaign.redeemed_count > 0">
            這檔活動已經有人兌換過，<strong>效果內容也已鎖定</strong>——要改請另開一檔新活動。
          </template>
          <template v-else>效果內容在<strong>第一次兌換之後</strong>鎖定。</template>
        </p>

        <template v-if="canEdit">
          <div class="card-head mt"><h3 class="section-title">可修改的設定</h3></div>
          <div class="e-grid">
            <label class="field">
              <span class="field-label">活動名稱</span>
              <input v-model="edit.name" maxlength="120" />
            </label>
            <label class="field">
              <span class="field-label">
                限量（{{ campaign.code_mode === 'unique' ? '可發放的子碼總數' : '兌換次數上限' }}）
              </span>
              <div class="e-inline">
                <input
                  v-model="edit.quantity"
                  type="number"
                  min="1"
                  class="e-num"
                  :disabled="edit.unlimited"
                />
                <label class="e-chk">
                  <input v-model="edit.unlimited" type="checkbox" /><span>不限量</span>
                </label>
              </div>
              <small class="hint">只能調高，不得低於已{{ campaign.code_mode === 'unique' ? '發放' : '兌換' }}的數量。</small>
            </label>
            <label class="field">
              <span class="field-label">開始（留空＝立即生效）</span>
              <input v-model="edit.startsAt" type="date" />
            </label>
            <label class="field">
              <span class="field-label">結束（含當天，留空＝不限）</span>
              <input v-model="edit.endsAt" type="date" />
            </label>
            <label v-if="campaign.kind !== 'grant'" class="field">
              <span class="field-label">適用對象</span>
              <select v-model="edit.targetLicense">
                <option value="any">不限</option>
                <option value="trial">只限試用授權</option>
                <option value="paid">只限正式授權</option>
              </select>
            </label>
            <label class="field">
              <span class="field-label">內部備註（不下發到客戶站台）</span>
              <textarea v-model="edit.notes" rows="2" />
            </label>
          </div>
          <!-- 批107：使用條件。**可改、不追溯**——只影響之後的兌換，已經兌過的不撤。 -->
          <div class="e-row mt">
            <span class="e-label">使用條件</span>
            <label class="e-chk">
              <input v-model="edit.oncePer" type="radio" value="customer" />
              <span>單一客戶一次</span>
            </label>
            <label class="e-chk">
              <input v-model="edit.oncePer" type="radio" value="deployment" />
              <span>單一部署一次</span>
            </label>
            <span class="subtle">改了只影響之後的兌換，已兌換的不會被撤回。</span>
          </div>
          <label class="e-chk mt">
            <input v-model="edit.stackable" type="checkbox" />
            <span>可與同類型兌換碼併用</span>
          </label>
          <div class="e-acts">
            <UiButton variant="primary" :loading="saving" @click="saveSettings()">儲存設定</UiButton>
          </div>
        </template>
        <p v-else-if="isReseller" class="subtle mt">
          活動內容由愛+1 維護。需要調整名額、期限或暫停，請聯絡我方窗口。
        </p>
      </div>

      <!-- ===== 代碼 ===== -->
      <div v-show="tab === 'codes'" class="card">
        <div class="card-head">
          <h3 class="section-title">代碼</h3>
          <span class="spacer" />
          <select v-model="codeStatusFilter" class="f-sel" @change="reloadCodes()">
            <option value="">全部狀態</option>
            <option value="issued">未兌換</option>
            <option value="redeemed">已兌換</option>
            <option value="void">已作廢</option>
          </select>
          <UiButton size="sm" :disabled="!codes.length" @click="exportCsv()">匯出 CSV</UiButton>
          <UiButton
            v-if="campaign.code_mode === 'unique'"
            variant="primary"
            size="sm"
            @click="openIssue()"
          >
            發放代碼
          </UiButton>
        </div>

        <p v-if="campaign.code_mode === 'shared'" class="subtle">
          這是<strong>共用碼</strong>活動：整檔只有一組代碼，大家共用，名額用「兌換次數」計。
          要停止發送就暫停活動（共用碼沒有「作廢」這回事）。
        </p>
        <p v-else class="subtle">
          共 {{ codeCounts.issued ?? 0 }} 組未兌換、{{ codeCounts.redeemed ?? 0 }} 組已兌換、
          {{ codeCounts.void ?? 0 }} 組已作廢。發出去的碼一定兌得了（發放時就扣名額，作廢會釋回）。
        </p>

        <div v-if="codesLoading && !codes.length" class="loading-wrap"><span class="spinner lg" /></div>
        <div v-else class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <thead>
              <tr>
                <th>代碼</th>
                <th>狀態</th>
                <th>發給誰</th>
                <th>發放人</th>
                <th>發放時間</th>
                <th>兌換時間</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in codes" :key="row.id">
                <td><code class="mono code-nw">{{ row.display_code }}</code></td>
                <td>
                  <span class="status-pill" :class="codeStatusClass(row.status)">
                    {{ codeStatusLabel(row.status) }}
                  </span>
                </td>
                <td class="small">
                  {{ row.distributed_to_customer_id ? store.customerName(row.distributed_to_customer_id) : '—' }}
                  <div v-if="row.distributed_note" class="sub">{{ row.distributed_note }}</div>
                </td>
                <td class="small">
                  {{ row.issued_by ?? '—' }}
                  <div class="sub">{{ realmLabel(row.issued_realm) }}</div>
                </td>
                <td class="small">{{ fmtDateTime(row.issued_at ?? row.created_at) }}</td>
                <td class="small">{{ fmtDateTime(row.redeemed_at) }}</td>
                <td class="acts-cell">
                  <div class="acts">
                    <UiButton size="sm" @click="copyCode(row.display_code)">複製</UiButton>
                    <UiButton
                      v-if="row.status === 'issued' && campaign.code_mode === 'unique'"
                      variant="danger"
                      size="sm"
                      @click="voidCode(row)"
                    >
                      作廢
                    </UiButton>
                  </div>
                </td>
              </tr>
              <tr v-if="!codes.length">
                <td colspan="7" class="subtle empty-row">
                  {{ campaign.code_mode === 'unique' ? '還沒有發放任何代碼。' : '沒有代碼資料。' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="codesCursor" class="more">
          <UiButton size="sm" :loading="codesLoading" @click="moreCodes()">載入更多</UiButton>
          <span class="subtle">已載入 {{ codes.length }} 組</span>
        </div>
      </div>

      <!-- ===== 兌換紀錄 ===== -->
      <div v-show="tab === 'redemptions'" class="card">
        <div class="card-head"><h3 class="section-title">兌換紀錄</h3></div>
        <p class="subtle">
          每一筆都記下<strong>誰用</strong>（兌換人）與<strong>用在哪</strong>（客戶、部署、通路）。
          <template v-if="isReseller">兌換人的 IP 屬我方稽核資料，不在此列出。</template>
        </p>
        <div v-if="redLoading && !redemptions.length" class="loading-wrap"><span class="spinner lg" /></div>
        <div v-else class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <thead>
              <tr>
                <th>兌換時間</th>
                <th>誰用</th>
                <th>客戶</th>
                <th>哪一台</th>
                <th>通路</th>
                <th v-if="!isReseller">IP</th>
                <th>效果</th>
                <th>到期日</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in redemptions" :key="r.redemption_id">
                <td class="small">{{ fmtDateTime(r.redeemed_at) }}</td>
                <td class="small">
                  {{ r.actor_email ?? '—' }}
                  <div class="sub">
                    {{ r.actor_name ?? '' }}
                    <span :title="r.actor_verified ? '經 Google 登入驗證' : '由產品端自報，未驗證'">
                      {{ r.actor_verified ? '已驗證' : '自報' }}
                    </span>
                  </div>
                </td>
                <td class="small">{{ r.customer_name ?? '—' }}</td>
                <td class="small">
                  <code class="mono" :title="r.instance_id">{{ r.instance_id.slice(0, 12) }}</code>
                  <div v-if="r.hostname" class="sub">{{ r.hostname }}</div>
                  <div v-if="r.public_url" class="sub">
                    <!-- 批185（FE-01）：部署端自報的網址只對 http(s) 渲染成連結，其餘純文字。 -->
                    <a
                      v-if="safeHttpUrl(r.public_url)"
                      :href="safeHttpUrl(r.public_url) ?? undefined"
                      target="_blank"
                      rel="noopener noreferrer"
                    >{{ r.public_url }}</a>
                    <span v-else class="mono">{{ r.public_url }}</span>
                  </div>
                </td>
                <td class="small">
                  {{ channelLabel(r.channel) }}
                  <div v-if="r.app_version" class="sub">v{{ r.app_version }}</div>
                </td>
                <td v-if="!isReseller" class="small mono">{{ r.client_ip ?? '—' }}</td>
                <td class="small">{{ redemptionEffect(r) }}</td>
                <td class="small">
                  <template v-if="r.effect_summary.new_expires_at">
                    {{ fmtDate(r.effect_summary.old_expires_at) }} →
                    <strong>{{ fmtDate(r.effect_summary.new_expires_at) }}</strong>
                  </template>
                  <template v-else>—</template>
                </td>
              </tr>
              <tr v-if="!redemptions.length">
                <td :colspan="isReseller ? 7 : 8" class="subtle empty-row">還沒有人兌換這檔活動。</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="redCursor" class="more">
          <UiButton size="sm" :loading="redLoading" @click="moreRedemptions()">載入更多</UiButton>
          <span class="subtle">已載入 {{ redemptions.length }} 筆</span>
        </div>
      </div>
    </template>

    <!-- 發放代碼 -->
    <UiModal :open="issueOpen" title="發放代碼" width="560px" @close="issueOpen = false">
      <template v-if="!justIssued.length">
        <p class="pm-note">
          一次最多 500 組。
          <template v-if="issueRemaining !== null">
            這檔活動還可以發 <strong>{{ issueRemaining }}</strong> 組
            （限量 {{ campaign?.quantity }}，已發 {{ campaign?.issued_count }}）。
          </template>
          <template v-else>本活動不限量。</template>
        </p>
        <div class="form-grid">
          <label class="field">
            <span class="field-label">數量</span>
            <input v-model.number="issueDraft.count" type="number" min="1" max="500" />
          </label>
          <label class="field">
            <span class="field-label">發給哪位客戶（選填，只是登記）</span>
            <select v-model="issueDraft.customerId">
              <option value="">（不指定）</option>
              <option v-for="c in store.customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c, { ownership: !auth.isReseller }) }}</option>
            </select>
            <small class="hint">
              這欄<strong>不限制</strong>誰能兌換——要限定對象請用代理商專屬或不重複子碼。
            </small>
          </label>
          <label class="field">
            <span class="field-label">發放備註</span>
            <input v-model="issueDraft.note" maxlength="200" placeholder="例如：2026 春季展攤位" />
          </label>
        </div>
      </template>
      <template v-else>
        <p class="pm-note">已發放 <strong>{{ justIssued.length }}</strong> 組，請複製給客戶：</p>
        <ul class="issued-list">
          <li v-for="c in justIssued" :key="c.id">
            <code class="mono">{{ c.display_code }}</code>
            <UiButton size="sm" @click="copyCode(c.display_code)">複製</UiButton>
          </li>
        </ul>
        <p class="hint">代碼清單也在「代碼」頁籤，可用〈匯出 CSV〉整批帶走。</p>
      </template>
      <template #foot>
        <UiButton @click="issueOpen = false">{{ justIssued.length ? '關閉' : '取消' }}</UiButton>
        <UiButton
          v-if="!justIssued.length"
          variant="primary"
          :loading="issuing"
          :disabled="!(issueDraft.count >= 1 && issueDraft.count <= 500)"
          @click="doIssue()"
        >
          發放
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  margin-bottom: var(--sp-4);
}
.title {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
}
.title h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.back {
  border: none;
  background: none;
  color: var(--fg-3);
  font-family: inherit;
  font-size: var(--fs-13);
  cursor: pointer;
  padding: 0;
}
.back:hover {
  color: var(--brand-600);
}
.head-acts {
  display: flex;
  gap: var(--sp-2);
}
.tabs {
  margin-bottom: var(--sp-3);
}
.err-line {
  margin: 0 0 var(--sp-3);
  color: var(--danger);
  font-size: var(--fs-12);
}
.info {
  margin: 0 0 var(--sp-3);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px var(--sp-4);
}
.info div {
  display: flex;
  gap: var(--sp-2);
  align-items: baseline;
  font-size: var(--fs-13);
}
.info dt {
  color: var(--fg-3);
  min-width: 76px;
  flex-shrink: 0;
}
.info dd {
  margin: 0;
  color: var(--fg);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
}
.locked-note {
  margin: 0;
  padding: var(--sp-2) var(--sp-3);
  border-radius: var(--r-sm);
  background: var(--bg);
  font-size: var(--fs-11);
  color: var(--fg-3);
  line-height: var(--lh-base);
}
.card-head.mt {
  margin-top: var(--sp-4);
}
.e-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
}
.e-inline {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
}
.e-num {
  width: 110px;
  text-align: right;
}
.e-chk {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-12);
  color: var(--fg-2);
  cursor: pointer;
}
/* 批107：使用條件那一列。 */
.e-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-3);
}
.e-row.mt {
  margin-top: var(--sp-3);
}
.e-label {
  font-size: var(--fs-12);
  font-weight: 600;
  color: var(--fg);
}
.e-chk.mt {
  margin-top: var(--sp-3);
}
.e-acts {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--sp-3);
}
.f-sel {
  min-width: 120px;
  height: var(--control-h);
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.small {
  font-size: var(--fs-12);
}
.sub {
  color: var(--fg-4);
  font-size: var(--fs-11);
  margin-top: 2px;
}
.hint {
  margin: 4px 0 0;
  font-size: var(--fs-11);
  color: var(--fg-4);
  line-height: var(--lh-base);
}
.mt {
  margin-top: var(--sp-3);
}
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}
.mono {
  font-family: ui-monospace, monospace;
  letter-spacing: 1px;
}
.mono.big {
  font-size: var(--fs-15);
  font-weight: 700;
  color: var(--brand-700);
}
.pm-note {
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-12);
  color: var(--fg-3);
  line-height: var(--lh-base);
}
.issued-list {
  list-style: none;
  margin: 0 0 var(--sp-2);
  padding: 0;
  max-height: 320px;
  overflow: auto;
}
.issued-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
  padding: 4px 0;
  border-bottom: 1px dashed var(--border-2);
}
@media (max-width: 700px) {
  .info,
  .e-grid {
    grid-template-columns: 1fr;
  }
  .page-head {
    flex-direction: column;
    align-items: stretch;
  }
}
/* 手機／平板（批171）：兌換碼不在連字號處折行（折成兩行就看不出是一組碼、也不好抄）。 */
@media (--compact) {
  .code-nw {
    white-space: nowrap;
  }
}
</style>
